import React, { useRef, useState } from 'react';
import Icon from './Icon';
import { Modal } from './ui/Modal';
import { useAuth } from '../contexts/AuthContext';
import PrimaryCurrencyField from './settings/PrimaryCurrencyField';
import { getActiveProjectId } from '../utils/session';
import { errorMessageOf } from '../utils/apiErrors';
import { useNotifications } from './NotificationProvider';
import type { ProjectCurrency } from '../utils/currency';
import '../styles/components/WorkspaceSwitcher.css';

// Mirrors Kalligo's "Switch project" affordance — one login can belong to
// more than one project now, so this is how a planner moves between the
// projects they manage. Hidden entirely when there's only one project, so
// the single-project user sees no change at all.
const WorkspaceSwitcher: React.FC = () => {
  const { workspaces, switchWorkspace, createWorkspace } = useAuth();
  const [open, setOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCurrency, setNewCurrency] = useState<ProjectCurrency>('');
  const { showError } = useNotifications();
  const [switching, setSwitching] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const activeProjectId = getActiveProjectId();
  const active = workspaces.find(w => w.project_id === activeProjectId);

  React.useEffect(() => {
    if (!open) return;
    const onClickAway = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, [open]);

  if (workspaces.length === 0) return null;

  const handleSwitch = async (projectId: string) => {
    if (projectId === activeProjectId || switching) return;
    setSwitching(true);
    try {
      await switchWorkspace(projectId);
    } catch (err) {
      showError(errorMessageOf(err, "Couldn't switch project. Please try again."));
      setSwitching(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim() || !newCurrency || switching) return;
    setSwitching(true);
    try {
      await createWorkspace(newName.trim(), newCurrency);
    } catch (err) {
      showError(errorMessageOf(err, "Couldn't create the project. Please try again."));
      setSwitching(false);
    }
  };

  const closeCreate = () => { setShowCreate(false); setNewName(''); setNewCurrency(''); };

  // Only one workspace exists and it's already active — nothing to switch
  // to, so don't clutter the header with a dropdown that does nothing.
  if (workspaces.length === 1) return null;

  return (
    <div className="ws-switcher" ref={rootRef}>
      <button
        type="button"
        className={`ws-switcher-trigger${switching ? ' ws-switcher-trigger--busy' : ''}`}
        onClick={() => setOpen(!open)}
        disabled={switching}
        title={switching ? 'Switching project…' : 'Switch project'}
      >
        {switching ? (
          <span className="ws-switcher-spinner" aria-hidden="true" />
        ) : (
          <Icon name="briefcase" size={16} />
        )}
        <span className="ws-switcher-name">
          {switching ? 'Switching…' : (active?.project_name ?? 'Select project')}
        </span>
        {!switching && <Icon name={open ? 'chevronUp' : 'chevronDown'} size={12} />}
      </button>

      {open && (
        <div className="ws-switcher-dropdown">
          {workspaces.map(ws => (
            <button
              key={ws.project_id}
              type="button"
              className={`ws-switcher-option${ws.project_id === activeProjectId ? ' ws-switcher-option--active' : ''}`}
              onClick={() => { setOpen(false); handleSwitch(ws.project_id); }}
            >
              <span className="ws-switcher-option-name">{ws.project_name}</span>
              <span className="ws-switcher-option-role">{ws.role}</span>
            </button>
          ))}
          <button
            type="button"
            className="ws-switcher-new"
            onClick={() => { setOpen(false); setShowCreate(true); }}
          >
            <Icon name="plus" size={14} />
            New project
          </button>
        </div>
      )}

      <Modal
        open={showCreate}
        onClose={closeCreate}
        title="New project"
        size="small"
        footer={
          <div className="modal-actions">
            <button className="primary-btn" onClick={handleCreate} disabled={!newName.trim() || !newCurrency || switching}>
              {switching ? 'Creating…' : 'Create'}
            </button>
            <button className="btn-cancel" onClick={closeCreate}>
              Cancel
            </button>
          </div>
        }
      >
        <p className="modal-note">Name this project after the couple or event you're planning.</p>
        <input
          type="text"
          className="ws-switcher-new-input"
          value={newName}
          onChange={e => setNewName(e.target.value)}
          placeholder="e.g. Alex & Sam's Wedding"
          autoFocus
        />
        <PrimaryCurrencyField value={newCurrency} locked={false} onChange={setNewCurrency} />
      </Modal>
    </div>
  );
};

export default WorkspaceSwitcher;
